import { useEffect } from "react";
import Cookies from "js-cookie";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import I18n, { t } from "@/i18n-js/instance";
import { communityApi } from "@/react/api";
import { communitiesPaths } from "@/react/helpers/routerPathHelpers";
import { useCurrentCommunityContext } from "@circle-react/apps/AuthenticationApp";
import { COUPON_CODE } from "@circle-react/components/OnboardingPage/helpers/constants";
import { CommunityPreview } from "@circle-react/components/OnboardingPage/layouts/OnboardingSplitLayout/components/CommunityPreview";
import { PageTitle } from "@circle-react/components/OnboardingPage/layouts/PageTitle";
import { supportedLocalesOptions } from "@circle-react/config/Locales";
import { Form } from "@circle-react-uikit/Form";
import { useToast } from "@circle-react-uikit/ToastV2";
import { useIsNewOnboardingFlow } from "../hooks/useIsNewOnboardingFlow";
import { useOnboardingTracker } from "../hooks/useOnboardingTracker";
import { useTurnstile } from "../hooks/useTurnstile";
import { OnboardingWrapper } from "../layouts/OnboardingWrapper";
import { CommunityNameInput } from "./CommunityNameInput";
import { CommunitySlugInput } from "./CommunitySlugInput";
import { Footer } from "./Footer";
import { TurnstileWrapper } from "./TurnstileWrapper";
import "./styles.scss";

const STEP_NUMBER = 1;
export const localeScope = "authentication_app.create_community";

interface SignUpFlowResponses {
  how_did_you_hear_about_circle?: string;
}

interface CommunityFormValues {
  [key: string]: unknown;
  sign_up_flow_responses?: SignUpFlowResponses;
}

export const CreateCommunityPage = () => {
  const { updateCurrentCommunity, suggestedLanguage } =
    useCurrentCommunityContext();
  const history = useHistory();
  const toast = useToast();

  const {
    turnstileRef,
    turnstileResult,
    dispatch,
    canUseTurnstile,
    shouldShowTurnstile,
  } = useTurnstile();

  const {
    totalSteps,
    isLoading: isOnboardingLoading,
    isNewOnboardingFlow,
  } = useIsNewOnboardingFlow();

  const { trackStepStarted, trackStepCompleted } = useOnboardingTracker({
    step_number: STEP_NUMBER,
    total_steps: totalSteps,
    step: "create_community",
    fields: [
      {
        name: t(`${localeScope}.form.name`),
        type: "text",
      },
      {
        name: t(`${localeScope}.form.slug`),
        type: "text",
      },
      ...(isNewOnboardingFlow
        ? [
            {
              name: t(
                "authentication_app.new_onboarding_flow_april_2025.create_community.how_did_you_hear",
              ),
              type: "text" as const,
            },
          ]
        : []),
    ],
    config: {
      trackStartedAutomatically: false,
    },
  });

  // Track step started after onboarding flow is loaded
  useEffect(() => {
    if (!isOnboardingLoading) {
      trackStepStarted();
    }
  }, [isOnboardingLoading, trackStepStarted]);

  const { mutateAsync: createCommunityAsync, isLoading } = useMutation<
    {
      id: string;
      name: string;
      slug: string;
      sign_up_flow_responses?: SignUpFlowResponses;
    },
    Error,
    { turnstile_token?: string; community: CommunityFormValues }
  >(data => communityApi.createCommunity(data), {
    onSuccess: data => {
      updateCurrentCommunity(data);
      trackStepCompleted([
        data.name,
        data.slug,
        ...(isNewOnboardingFlow
          ? [data?.sign_up_flow_responses?.how_did_you_hear_about_circle || ""]
          : []),
      ]);
      history.push(communitiesPaths.questions(data.id));
    },
    onError: error => {
      if (typeof error === "object" && error !== null && "message" in error) {
        toast.error(String(error.message));
      }
    },
  });

  const buildCommunityPayloadWithSignUpFlowResponses = (
    community: CommunityFormValues,
  ): CommunityFormValues => {
    const { sign_up_flow_responses, ...rest } = community;
    return sign_up_flow_responses?.how_did_you_hear_about_circle?.trim()
      ? { ...rest, sign_up_flow_responses }
      : rest;
  };

  const handleSubmit = async (data: { community: CommunityFormValues }) => {
    try {
      const turnstileToken = canUseTurnstile
        ? turnstileRef.current?.getResponse()
        : null;

      const communityPayload = buildCommunityPayloadWithSignUpFlowResponses(
        data.community,
      );

      const payload = {
        ...data,
        community: communityPayload,
        ...(turnstileToken && { turnstile_token: turnstileToken }),
      };
      await createCommunityAsync(payload);
    } catch (error) {
      if (typeof error === "object" && error !== null && "message" in error) {
        const errorMessage =
          String(error?.message) || t("community.creation_failed.generic");
        toast.error(errorMessage);
      }
    }
  };

  const couponCode = Cookies.get(COUPON_CODE);

  const defaultFormState = {
    community: {
      name: "",
      slug: "",
      plan_id: "",
      coupon_code: couponCode,
    },
  };

  return (
    <Form defaultValues={defaultFormState} onSubmit={handleSubmit}>
      {({ watch }) => {
        // eslint-disable-next-line i18next/no-literal-string -- This is a form field path for accessing user data, not a static string that needs translation
        const communityName = watch("community[name]") || "";
        return (
          <OnboardingWrapper
            title={t(`${localeScope}.title`)}
            subtitle={t(`${localeScope}.subtitle`)}
            progress={{
              new: {
                currentStep: STEP_NUMBER,
                totalSteps: totalSteps,
              },
              legacy: `${STEP_NUMBER}/${totalSteps}`,
            }}
            rightPanel={
              <CommunityPreview
                communityName={communityName}
                animateOnMount
                shimmerTitle
              />
            }
            background="light"
          >
            <PageTitle title={t(`${localeScope}.page_title`)} />
            <div>
              <CommunityNameInput />
              <CommunitySlugInput />
              {isNewOnboardingFlow && (
                <Form.Item
                  name="community[sign_up_flow_responses][how_did_you_hear_about_circle]"
                  label={t(
                    `authentication_app.new_onboarding_flow_april_2025.create_community.how_did_you_hear`,
                  )}
                  hideDescription
                  hideBorder
                >
                  <Form.Input data-testid="how_did_you_hear_about_circle" />
                </Form.Item>
              )}
              {suggestedLanguage && (
                <Form.Item
                  name="community[locale]"
                  label={t(`${localeScope}.form.locale`)}
                  hideDescription
                  hideBorder
                  rules={{
                    required: I18n.t(
                      `${localeScope}.form.errors.locale_required`,
                    ),
                  }}
                  tooltipText={t(`${localeScope}.form.locale_tooltip`)}
                >
                  <Form.SelectNative
                    name="locale"
                    options={supportedLocalesOptions}
                    defaultValue={suggestedLanguage}
                  />
                </Form.Item>
              )}

              <Form.Item
                hiddenField
                name="community[coupon_code]"
                hideDescription
                hideBorder
                hideLabel
              >
                <Form.Input type="hidden" />
              </Form.Item>

              <Form.Item
                hiddenField
                name="community[plan_id]"
                hideDescription
                hideBorder
                hideLabel
              >
                <Form.Input type="hidden" />
              </Form.Item>

              <div className="mt-1 flex w-full items-center justify-center">
                <TurnstileWrapper
                  turnstileRef={turnstileRef}
                  shouldShowTurnstile={shouldShowTurnstile}
                  dispatch={dispatch}
                />
              </div>

              <Footer
                isDisabled={
                  (turnstileResult.requiresInteraction &&
                    !turnstileResult.isInteractionSuccessful) ||
                  turnstileResult.hasFailed
                }
                isLoading={isLoading || turnstileResult.isLoading}
              />
            </div>
          </OnboardingWrapper>
        );
      }}
    </Form>
  );
};

export default CreateCommunityPage;
